import React, { useEffect, useState } from 'react';
import { Bot, Cog, FileText, Users } from 'lucide-react';

interface ProcessMapLoaderProps {
  selectedLevel: number;
  operation: 'generating' | 'switching' | 'loading' | 'regenerating' | 'syncing';
  processLevels: any[];
  progress?: number;
  stage?: string;
}

const ProcessMapLoader: React.FC<ProcessMapLoaderProps> = ({
  selectedLevel,
  operation,
  processLevels,
  progress = 0,
  stage
}) => {
  const [currentStage, setCurrentStage] = useState(0);

  const levelInfo = processLevels.find(l => l.value === selectedLevel);
  const levelColor = selectedLevel === 1 ? 'purple' : 
                    selectedLevel === 2 ? 'blue' : 
                    selectedLevel === 3 ? 'green' : 'orange';

  const stages = {
    generating: [
      { icon: FileText, text: 'Analyzing process synthesis data', duration: 3000 },
      { icon: Users, text: 'Identifying stakeholders and roles', duration: 4000 },
      { icon: Cog, text: 'Building BPMN structure', duration: 5000 },
      { icon: Bot, text: 'Applying AI intelligence', duration: 6000 },
      { icon: FileText, text: 'Finalizing process map', duration: 2000 }
    ],
    regenerating: [
      { icon: FileText, text: 'Clearing existing map data', duration: 2000 },
      { icon: Users, text: 'Re-analyzing stakeholder assignments', duration: 4000 },
      { icon: Cog, text: 'Rebuilding process structure', duration: 5000 },
      { icon: Bot, text: 'Regenerating with latest data', duration: 4000 }
    ],
    syncing: [
      { icon: Users, text: 'Fetching stakeholders from Stakeholders Tab', duration: 2000 },
      { icon: Cog, text: 'Matching swimlanes with stakeholders', duration: 3000 },
      { icon: Bot, text: 'Regenerating process map with AI', duration: 5000 },
      { icon: FileText, text: 'Finalizing synced process map', duration: 3000 }
    ],
    switching: [
      { icon: FileText, text: 'Loading level data', duration: 1500 },
      { icon: Cog, text: 'Preparing visualization', duration: 1000 }
    ],
    loading: [
      { icon: FileText, text: 'Loading process maps', duration: 2000 }
    ]
  };

  const currentStages = stages[operation] || stages.loading;

  useEffect(() => {
    const totalDuration = currentStages.reduce((sum, stage) => sum + stage.duration, 0);
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += 100;

      // Update current stage
      let stageElapsed = 0;
      for (let i = 0; i < currentStages.length; i++) {
        if (elapsed <= stageElapsed + currentStages[i].duration) {
          setCurrentStage(i);
          break;
        }
        stageElapsed += currentStages[i].duration;
      }

      if (elapsed >= totalDuration) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [operation, currentStages]);

  const getOperationTitle = () => {
    switch (operation) {
      case 'generating':
        return `Generating ${levelInfo?.name}`;
      case 'regenerating':
        return `Regenerating ${levelInfo?.name}`;
      case 'syncing':
        return `Syncing with Stakeholders`;
      case 'switching':
        return `Switching to ${levelInfo?.name}`;
      case 'loading':
        return 'Loading Process Maps';
      default:
        return 'Processing...';
    }
  };

  const getOperationDescription = () => {
    switch (operation) {
      case 'generating':
        return `Creating a new ${levelInfo?.description.toLowerCase()} process map using AI analysis`;
      case 'regenerating':
        return `Rebuilding the ${levelInfo?.description.toLowerCase()} process map with updated data`;
      case 'syncing':
        return `Regenerating process map using only stakeholders from Stakeholders & Systems tab`;
      case 'switching':
        return `Loading ${levelInfo?.description.toLowerCase()} process map data`;
      case 'loading':
        return 'Fetching existing process map data from database';
      default:
        return 'Please wait while we process your request...';
    }
  };

  const CurrentIcon = currentStages[currentStage]?.icon || Bot;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 mx-4 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-${levelColor}-100 mb-4`}>
            <div className={`w-6 h-6 rounded-full bg-${levelColor}-500`}></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {getOperationTitle()}
          </h2>
          <p className="text-gray-600 text-sm">
            {getOperationDescription()}
          </p>
        </div>

        {/* Animated Dots Loading */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex flex-col items-center">
            {/* Icon */}
            <div className="mb-6">
              <CurrentIcon className={`w-16 h-16 text-${levelColor}-500 animate-pulse`} />
            </div>
            
            {/* Three Animated Dots */}
            <div className="flex items-center space-x-2">
              <div 
                className={`w-4 h-4 bg-${levelColor}-500 rounded-full animate-bounce`}
                style={{ animationDelay: '0ms' }}
              ></div>
              <div 
                className={`w-4 h-4 bg-${levelColor}-500 rounded-full animate-bounce`}
                style={{ animationDelay: '150ms' }}
              ></div>
              <div 
                className={`w-4 h-4 bg-${levelColor}-500 rounded-full animate-bounce`}
                style={{ animationDelay: '300ms' }}
              ></div>
            </div>
          </div>
        </div>

        {/* Current Stage */}
        <div className="text-center mb-6">
          <p className="text-gray-700 font-medium mb-2">
            {stage || currentStages[currentStage]?.text || 'Processing...'}
          </p>
          
          {/* Stage indicators */}
          <div className="flex justify-center space-x-2">
            {currentStages.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index <= currentStage 
                    ? `bg-${levelColor}-500` 
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Processing info */}
        <div className={`bg-${levelColor}-50 border border-${levelColor}-200 rounded-lg p-4`}>
          <div className="flex items-start space-x-3">
            <Bot className={`w-5 h-5 text-${levelColor}-600 mt-0.5`} />
            <div>
              <p className={`text-sm font-medium text-${levelColor}-800 mb-1`}>
                AI Processing in Progress
              </p>
              <p className={`text-xs text-${levelColor}-700`}>
                {operation === 'generating' && 'This typically takes 15-30 seconds depending on data complexity.'}
                {operation === 'regenerating' && 'Rebuilding with the latest synthesis data and improvements.'}
                {operation === 'syncing' && 'Syncing swimlanes with stakeholders from the Stakeholders tab. This may take 15-30 seconds.'}
                {operation === 'switching' && 'Loading cached data for faster access.'}
                {operation === 'loading' && 'Retrieving your saved process maps from the database.'}
              </p>
            </div>
          </div>
        </div>

        {/* Estimated time */}
        {(operation === 'generating' || operation === 'regenerating' || operation === 'syncing') && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Estimated time remaining: 15-30 seconds
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessMapLoader;